import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Action, Getter, Mutation } from 'vuex-class'
import moment from 'moment'

import ApiRequest from '@/core/ApiRequest'
import EventModel, { EventStatus } from '@/core/models/Event'
import RoomModel, { TABLES_ALL } from '@/core/models/Room'
import ResponseModel from '@/core/models/ResponseModel'
import { MenuGroup } from '@/core/models/Menu'
import { ADD_EVENT_ITEM, FETCH_MENU_LIST, FETCH_ROOMS_LIST } from '@/store/root/actions'
import { GET_API_REQUEST, GET_ROOMS_LIST, GET_MENU_LIST, IS_HISTORY_DIALOG_SHOWN } from '@/store/root/getters'
import { SET_HISTORY_DIALOG_SHOWN } from '@/store/root/mutations'

import EventHistory from '@/components/shared/event-history/EventHistory.vue'


@Component({
  components: { EventHistory }
})
export default class Event extends Vue {
  public snackbarTimeout = 1750
  public snackbarText = ''
  public snackbarShown = false

  private DISPLAY_DATE_FORMAT = 'DD-MM-YYYY'
  public room: RoomModel | null = null;
  public tableNumbers: Array<string> = [];
  public options: Array<{ name: string, amount: string }> = [];
  public optionsItems: Array<string> = ['Фуршет', 'Выездная церемония', 'Выездная церемония перед главным залом', 'Оформление зала и президиума', 'Оформление президиума', 'Буквы имен молодоженов', 'Схема рассадки гостей', 'Селфи зеркало', 'Другое']; // todo: move to const
  public persons: Array<{ count: string, price: string }> = [];
  public dateFrom: string = '';
  public dateTo: string = '';
  public timeFrom: any = '10:00';
  public timeTo: any = '23:00';
  public eventType: string = '';
  public files: any = [];
  public menu: Array<string> = [];
  public attachmentLoading = false;
  public isConfirmDialogShown = false;
  public warningMessages: Array<string> = [];

  public attachments: Array<{ id: string, name: string, mimetype: string, size: string }> = [];

  public contacts: Array<{ name: string, phone: string }> = [
    { name: '', phone: '' },
    { name: '', phone: '' },
    { name: '', phone: '' },
    { name: '', phone: '' },
    { name: '', phone: '' },
  ];

  public payments: Array<{ date: string | null, owner: string, amount: string, type: string }> = [
    {
      date: null,
      owner: '',
      amount: '',
      type: '',
    },
  ];

  public refunds: Array<{ date: string | null, owner: string, amount: string, type: string }> = [
    {
      date: null,
      owner: '',
      amount: '',
      type: '',
    },
  ];

  public paymentDateMenus: Array<boolean> = [false];
  public refundDateMenus: Array<boolean> = [false];

  public clientComments: any = '';
  public status: EventStatus = EventStatus.ORDERED;
  public statusCancellationReason: any = '';
  public statusConfirmationDate: string | null = null;
  public managerComments: any = '';
  public EventStatus: any = EventStatus;

  public dateFromMenu = false;
  public timeFromMenu = false;
  public dateToMenu = false;
  public timeToMenu = false;
  public statusConfirmationDateMenu = false;
  public errorMessage: string | null = '';
  public errorMessagePhones: Array<string | null> = ['', '', '', '', ''];
  public isLoading = false;
  public eventIsFetched = false;
  public canEdit = false;
  public canForced = true;
  public paymentsPaid: number | null = null;
  public paymentsTotal: number | null = null;
  public paymentsLeast: number | null = null;
  public refundsPaid: number | null = null;
  public refundsTotal: number | null = null;
  public refundsLeast: number | null = null;

  public tablesLastKey: string = '';
  public unavailableTables: Array<string> = [];

  public date: string = (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000))
    .toISOString().substr(0, 10);

  /**
   * Property that indicates ID of shown event.
   */
  @Prop({ default: null })
  public id: string | null;

  /**
   * Property that indicates is edit mode enabled.
   */
  @Prop({ default: true })
  public editable: boolean;

  /**
   * Fetch rooms list.
   */
  @Action(FETCH_ROOMS_LIST)
  public fetchRoomsList:
    () => void;

  /**
   * Fetch menu list.
   */
  @Action(FETCH_MENU_LIST)
  public fetchMenuList:
    () => void;

  /**
   * Add event item.
   */
  @Action(ADD_EVENT_ITEM)
  public addEventItem:
    /**
     * @param payload      Payload with event to add.
     *
     * @return   Resolved promise.
     */
    (payload: { event: EventModel, forced: boolean | null }) => Promise<ResponseModel>

  /**
   * Get api request object, based on global Vuex state.
   */
  @Getter(GET_API_REQUEST)
  public getApiRequest: ApiRequest

  /**
   * Get rooms list, based on global Vuex state.
   */
  @Getter(GET_ROOMS_LIST)
  public getRoomsList: RoomModel[];

  /**
   * Get menu list, based on global Vuex state.
   */
  @Getter(GET_MENU_LIST)
  public getMenuList: MenuGroup[];

  /**
   * Get is history dialog shown flag, based on global Vuex state.
   */
  @Getter(IS_HISTORY_DIALOG_SHOWN)
  public isHistoryDialogShown: boolean

  /**
   * Sets is history dialog shown flag state.
   */
  @Mutation(SET_HISTORY_DIALOG_SHOWN)
  public setHistoryDialogShown: (isHistoryDialogShown: boolean) => void

  /**
   * Room ID getter.
   */
  public get roomId (): string | null {
    return this.room
      ? this.room.id
      : null
  }

  /**
   * Room ID setter.
   */
  public set roomId (value: string | null) {
    this.room = this.getRoomsList.find((room) => room.id === value) || null
    this.tableNumbers = [TABLES_ALL]
  }

  /**
   * Returns list of tables for current room.
   */
  public getTablesList () {
    if (this.room === null) {
      return []
    }

    const tables: Array<any> = Array.from(
      { length: this.room!.tables_count },
      (_, i) => {
        const tableNum = String(i + 1)
        const disabled = this.unavailableTables.includes(tableNum) || this.unavailableTables.includes('all')
        return {
          text: disabled ? `${tableNum} (занят)` : tableNum,
          value: tableNum,
          disabled,
        }
      })
    tables.unshift({
      text: TABLES_ALL,
      value: TABLES_ALL,
    })
    return tables
  }

  /**
   * Returns event types list.
   */
  public getEventTypes () {
    return [
      'Свадьба',
      'Сватовство',
      'День рождения',
      'Крестины',
      'Юбилей',
      'Выпускной',
      'Стодневка',
      'Встреча одноклассников',
      'Корпоратив',
      'Конференция',
      'Выставка',
      'Показ',
      'Концерт',
      'Нишан',
      'Бешик-той',
      'Дуа',
      'Ифтар',
      'Поминки',
      'Другое',
      'Служебное: ремонт',
    ]
  }

  /**
   * Returns statuses list.
   */
  public getStatusesList () {
    const statuses = [
      { status: EventStatus.PREORDERED, title: 'Предзаказ' }, // TODO: move to models
      { status: EventStatus.ORDERED, title: 'Заказ' },
    ]
    if (this.id) {
      statuses.push({ status: EventStatus.CANCELED, title: 'Отмененный заказ' })
    }
    return statuses
  }

  /**
   * Allowed step for time picker.
   *
   * @param min
   */
  public timePickerAllowedStep (min: number) {
    return min % 5 === 0
  }

  public onDateFromChange (value: string) {
    this.dateFromMenu = false
    console.log(`onDateFromChange(${value})`)
    if (this.dateTo === '' || this.dateTo < this.dateFrom) {
      this.dateTo = value
    }
  }

  public async onAttachmentChanged (value: any) {
    console.log('onAttachmentChanged=', value)
    if (!value || !value.length) {
      // Cleared
    }

    this.attachmentLoading = true

    const formData = new FormData()

    let counter = 0
    for (const file of this.files) {
      formData.append(`file${counter++}`, file)
    }
    const response = await this.getApiRequest.send(
      'POST',
      'attachments',
      formData
    )
    this.attachmentLoading = false

    if (!response || !response.data) {
      return
    }

    for (const attachment of response.data) {
      this.attachments.push({ ...attachment })
    }

    this.files = []
  }

  public addContactRow () {
    this.contacts.push({ name: '', phone: '' })
  }

  public removeContactRow (index: number) {
    if (index > 0) {
      this.contacts.splice(index, 1)
    } else {
      if (this.contacts.length > 1) { // exists yet rows
        this.contacts.splice(index, 1)
      } else {
        this.contacts[0] = { name: '', phone: '' }
      }
    }
  }

  public copyPhone (index: number) {
    try {
      navigator.clipboard.writeText(this.contacts[index].phone)
      this.snackbarText = 'Номер скопирован'
      this.snackbarShown = true
    } catch (e) {
      console.log('Error no copy phone', e.message)
    }
  }

  public onPhoneInput (formattedNumber: string, options: any, index: number) {
    const { number, valid, country } = options
    this.contacts[index].phone = number.international
    if (!this.eventIsFetched) {
      this.errorMessagePhones[index] = null
      return
    }

    this.errorMessagePhones[index] = valid
      ? null
      : 'Номер указан неверно'
  }


  public addRefundRow () {
    this.refunds.push({ date: null, owner: '', amount: '', type: '' })
    this.refundDateMenus.push(false)
  }

  public removeRefundRow (index: number) {
    if (index > 0) {
      this.refunds.splice(index, 1)
      this.refundDateMenus.splice(index, 1)
    } else {
      if (this.refunds.length > 1) { // exists yet rows
        this.refunds.splice(index, 1)
        this.refundDateMenus.splice(index, 1)
      } else {
        this.refunds[0] = { date: null, owner: '', amount: '', type: '' }
        this.refundDateMenus[0] = false
      }
    }
  }

  public addPaymentRow () {
    this.payments.push({ date: null, owner: '', amount: '', type: '' })
    this.paymentDateMenus.push(false)
  }

  public removePaymentRow (index: number) {
    if (index > 0) {
      this.payments.splice(index, 1)
      this.paymentDateMenus.splice(index, 1)
    } else {
      if (this.payments.length > 1) { // exists yet rows
        this.payments.splice(index, 1)
        this.paymentDateMenus.splice(index, 1)
      } else {
        this.payments[0] = { date: null, owner: '', amount: '', type: '' }
        this.paymentDateMenus[0] = false
      }
    }
  }


  public addOptionRow () {
    this.options.push({ name: '', amount: '' })
  }

  public removeOptionRow (index: number) {
    if (index > 0) {
      this.options.splice(index, 1)
    } else {
      if (this.options.length > 1) { // exists yet rows
        this.options.splice(index, 1)
      } else {
        this.options[0] = { name: '', amount: '' }
      }
    }
  }


  public addPersonRow () {
    this.persons.push({ count: '', price: '' })
  }

  public removePersonRow (index: number) {
    if (index > 0) {
      this.persons.splice(index, 1)
    } else {
      if (this.persons.length > 1) { // exists yet rows
        this.persons.splice(index, 1)
      } else {
        this.persons[0] = { count: '', price: '' }
      }
    }
  }

  public attachmentRules (value: any) {
    return !value || !value.length || value[0].size < 20 * 1024 * 1024 || 'Размер не должен превышать 20 Мб!'
  }

  public downloadFile (e: any, attachment: { id: string, name: string, mimetype: string, size: string }) {
    const fileUrl = `${this.getApiRequest.apiUrl}/attachments/${attachment.id}`
    console.log(`fileUrl=${fileUrl}`)
    console.log('downloadFile=', attachment, e)
    // @ts-ignore
    window.open(fileUrl, '_blank').focus()
  }

  public removeFile (index: number) {
    this.attachments.splice(index, 1)
  }

  /**
   * Form submit handler.
   */
  public async formSubmit (forced: boolean) {
    console.log('formSubmit')
    this.errorMessage = ''

    // todo: move to vue-validate rules
    if (!this.room) {
      this.errorMessage = 'Ошибка: не указан зал!'
      return
    }

    let validationError
    validationError = this.validateDates()
    validationError = validationError || this.validateContacts()
    validationError = validationError || this.validatePayments()
    validationError = validationError || this.validateRefunds()
    if (validationError) {
      this.errorMessage = validationError
      return
    }

    const event = new EventModel()
    if (this.id) {
      event.id = this.id
    }
    event.room_id = this.room?.id
    event.table_numbers = this.tableNumbers.includes(TABLES_ALL)
      ? 'all'
      : this.tableNumbers.join(',')

    event.contacts = []
    for (const [index, contact] of this.contacts.entries()) {
      if (index > 0 && !contact.name && !contact.phone) { continue }
      event.contacts.push(contact)
    }

    event.refunds = []
    for (const [index, refund] of this.refunds.entries()) {
      if (index > 0 && !refund.amount && !refund.date && !refund.owner && !refund.type) {
        continue
      }

      refund.date = this.refunds[index].date && this.refunds[index].date !== ''
        ? (new Date(this.refunds[index].date!)).toISOString()
        : null
      event.refunds.push(refund)
    }

    event.payments = []
    for (const [index, payment] of this.payments.entries()) {
      if (index > 0 && !payment.amount && !payment.date && !payment.owner && !payment.type) {
        continue
      }

      payment.date = this.payments[index].date && this.payments[index].date !== ''
        ? (new Date(this.payments[index].date!)).toISOString()
        : null
      event.payments.push(payment)
    }

    event.options = []
    for (const [index, option] of this.options.entries()) {
      if (!option.name) { continue }
      event.options.push(option)
    }
    event.options = event.options.length > 0 ? event.options : null

    event.persons = []
    for (const [index, person] of this.persons.entries()) {
      if (!person.count && !person.price) { continue }
      event.persons.push(person)
    }
    event.persons = event.persons.length > 0 ? event.persons : null

    const menuItems = this.menuItems()
    const menu = []
    if (this.menu.length > 0) {
      for (const itemId of this.menu) {
        const item = menuItems.find((item) => item.value === itemId)
        if (!item) { continue }
        menu.push({ id: itemId, title: item.text })
      }
    }
    event.menu = menu

    event.client_comments = this.clientComments
    event.type = this.eventType
    event.date_from = (new Date(this.dateFrom + 'T' + this.timeFrom)).toISOString()
    event.date_to = (new Date(this.dateTo + 'T' + this.timeTo)).toISOString()
    event.status_confirmation_date = this.statusConfirmationDate && this.statusConfirmationDate !== ''
      ? (new Date(this.statusConfirmationDate!)).toISOString()
      : null
    event.manager_comments = this.managerComments
    event.status = this.status
    event.attachments = this.attachments

    this.isLoading = true
    try {
      const result = await this.addEventItem({ event, forced })
      this.isLoading = false
      this.canForced = true
      if (result?.data?.warnings) {
        this.isConfirmDialogShown = true
        this.warningMessages = result.data.warnings
        return
      }
      if (result?.data?.error) {
        this.canForced = false
        this.isConfirmDialogShown = true
        this.warningMessages = [result.data.error]
        return
      }

      if (!this.id) {
        console.log('goto home', moment(event.date_from).format('YYYY-MM-DD'))
        this.$router.push({
          name: 'home',
          query: { date: moment(event.date_from).format('YYYY-MM-DD') },
        })
      } else {
        console.log('goto self')
        this.$router.go(0)
      }
    } catch (error) {
      this.isLoading = false
      if (error.error && error.error === 'Overbooking') {
        this.errorMessage = 'Overbooking: Выбранные столы уже забронированы. Выберите другие столы или измените дату.'
      }
    }
  }

  private validateDates (): string | null {
    if (!this.dateFrom || this.dateFrom === '') {
      return 'Ошибка: не указана дата начала бронирования!'
    }
    if (!this.dateTo || this.dateTo === '') {
      return 'Ошибка: не указана дата окончания бронирования!'
    }

    if (this.status === EventStatus.PREORDERED && !this.statusConfirmationDate) {
      return 'Ошибка: не указана дата подтверждения бронирования!'
    }
    return null
  }

  private validateContacts (): string | null {
    for (const [index, contact] of this.contacts.entries()) {
      if (index > 0 && !contact.name && !contact.phone) {
        continue
      }

      if (!contact.name) {
        return `Ошибка: не указано имя ${index > 0 ? 'контакта ' + (index + 1).toString() : 'клиента'}!`
      }
      if (!contact.phone) {
        return `Ошибка: не указан телефон ${index > 0 ? 'контакта ' + (index + 1).toString() : 'клиента'}!`
      }
    }
    return null
  }

  private validateRefunds (): string | null {
    for (const [index, refund] of this.refunds.entries()) {
      if (!refund.amount && !refund.date && !refund.owner && !refund.type) {
        continue
      }
      if (!refund.amount || +refund.amount < 1 || !Number.parseInt(refund.amount)) {
        return `Ошибка: в возврате ${index + 1} некорректная сумма!`
      }
      if (!refund.date) {
        return `Ошибка: в возврате ${index + 1} не указана дата возврата!`
      }
      if (!refund.owner) {
        return `Ошибка: в возврате ${index + 1} не указано кто вернул!`
      }
      if (!refund.type) {
        return `Ошибка: в возврате ${index + 1} не указан способ возврата!`
      }
    }

    return null
  }

  private validatePayments (): string | null {
    for (const [index, payment] of this.payments.entries()) {
      if (!payment.amount && !payment.date && !payment.owner && !payment.type) {
        continue
      }
      if (!payment.amount || +payment.amount < 1 || !Number.parseInt(payment.amount)) {
        return `Ошибка: в оплате ${index + 1} некорректная сумма!`
      }
      if (!payment.date) {
        return `Ошибка: в оплате ${index + 1} не указана дата оплаты!`
      }
      if (!payment.owner) {
        return `Ошибка: в оплате ${index + 1} не указано кому оплачено!`
      }
      if (!payment.type) {
        return `Ошибка: в оплате ${index + 1} не указан способ оплаты!`
      }
    }

    return null
  }

  /**
   * Fetch current event and fill component inputs.
   */
  public async fetchCurrentEvent () {
    const response = await this.getApiRequest.send(
      'GET',
      'events/' + encodeURIComponent(this.id!),
    )
    if (!response || !response.data) { return }

    const event = response.data
    console.log(event)
    this.eventIsFetched = true
    this.room = this.getRoomsList.find((room) => room.id === event.room_id) || null
    const tableItems = event.table_numbers.split(',')
    this.tableNumbers = tableItems.includes('all') ? [TABLES_ALL] : tableItems

    this.contacts = event.contacts
    this.options = event.options && event.options.length > 0 ? event.options : [{ name: '', amount: '' }]
    this.persons = event.persons && event.persons.length > 0 ? event.persons : [{ count: '', price: '' }]
    const menu = []
    if (event.menu?.length > 0) {
      for (const menuItem of event.menu) {
        menu.push(menuItem.id)
      }
    }
    this.menu = menu

    this.clientComments = event.client_comments
    this.eventType = event.type

    const dateFrom = new Date(event.date_from)
    const dateTo = new Date(event.date_to)
    this.dateFrom = moment(dateFrom.toISOString()).format('YYYY-MM-DD')
    this.dateTo = moment(dateTo.toISOString()).format('YYYY-MM-DD')
    this.timeFrom = dateFrom.toLocaleTimeString()
    this.timeTo = dateTo.toLocaleTimeString()

    this.status = event.status
    const statusConfirmationDate = new Date(event.status_confirmation_date)
    this.statusConfirmationDate = !isNaN(Date.parse(event.status_confirmation_date))
      ? moment(statusConfirmationDate.toISOString()).format('YYYY-MM-DD')
      : null

    this.payments = []
    for (const [index, payment] of event.payments.entries()) {
      payment.date = !isNaN(Date.parse(payment.date))
        ? moment(payment.date).format('YYYY-MM-DD')
        : null
      this.payments.push(payment)
    }

    this.refunds = []
    for (const [index, refund] of event.refunds.entries()) {
      refund.date = !isNaN(Date.parse(refund.date))
        ? moment(refund.date).format('YYYY-MM-DD')
        : null
      this.refunds.push(refund)
    }
    if (this.refunds.length === 0) {
      this.refunds.push({
        amount: '',
        date: null,
        owner: '',
        type: '',
      })
    }

    this.managerComments = event.manager_comments
    this.attachments = event.attachments || []
  }

  /**
   * Fetch current room.
   */
  private async fetchCurrentRoom () {
    const from = moment(this.dateFrom + 'T' + this.timeFrom).format()
    const to = moment(this.dateTo + 'T' + this.timeTo).format()
    const event_id = this.id || ''
    const response = await this.getApiRequest.send(
      'GET',
      'rooms/' + encodeURIComponent(this.room!.id) +
      '/?from=' + encodeURIComponent(from) +
      '&to=' + encodeURIComponent(to) +
      '&event_id=' + encodeURIComponent(event_id),
    )
    if (!response || !response.data) { return }
    return response.data
  }

  /**
   * Fetch current room.
   */
  private async fetchUnavailableTables () {
    if (this.room === null) { return }
    const eventKey = this.room.id + this.dateFrom + this.timeFrom + this.dateTo + this.timeTo
    if (this.tablesLastKey !== eventKey) {
      const room = await this.fetchCurrentRoom()
      this.unavailableTables = room.unavailable_tables
      this.tablesLastKey = eventKey
    }
  }

  /**
   * Show event history.
   */
  public showHistory () {
    this.setHistoryDialogShown(true)
  }

  public recalculateTotals () {
    let summary = 0
    for (const [index, payment] of this.payments.entries()) {
      if (Number.parseInt(payment.amount)) {
        summary += +payment.amount
      }
    }

    let summaryRefunds = 0
    for (const [index, refund] of this.refunds.entries()) {
      if (Number.parseInt(refund.amount)) {
        summaryRefunds += +refund.amount
      }
    }

    let optionsSummary = 0
    if (this.options && this.options.length > 0) {
      for (const [index, option] of this.options.entries()) {
        if (Number.parseInt(option.amount)) {
          optionsSummary += +option.amount
        }
      }
    }

    let personsSummary = 0
    // let menuSummary = 0
    let error = true
    if (this.persons && this.persons.length > 0) {
      for (const [index, person] of this.persons.entries()) {
        if (Number.parseInt(person.count) && Number.parseInt(person.price)) {
          error = false
          personsSummary += +person.count * +person.price
        }
      }
    }

    this.refundsTotal = summary
    this.refundsPaid = summaryRefunds
    this.refundsLeast = this.refundsTotal - this.refundsPaid

    this.paymentsPaid = summary
    if (!error) {
      this.paymentsTotal = personsSummary + optionsSummary
      this.paymentsLeast = this.paymentsTotal - this.paymentsPaid
      return
    }

    this.paymentsTotal = null
    this.paymentsLeast = null
  }

  public addForced () {
    this.isConfirmDialogShown = false
    this.formSubmit(true)
  }

  @Watch('tableNumbers')
  public onShownChanged (value: Array<string>, oldvalue: Array<string>) {
    if (value.includes(TABLES_ALL) && value.length > 1) {
      if (!oldvalue.includes(TABLES_ALL)) {
        this.tableNumbers = [TABLES_ALL]
      } else {
        this.tableNumbers = this.tableNumbers.filter(tableName => tableName !== TABLES_ALL)
      }
    }
  }

  @Watch('payments')
  public onPaymentsChanged () {
    this.recalculateTotals()
  }

  @Watch('refunds')
  public onRefundsChanged () {
    this.recalculateTotals()
  }

  @Watch('persons')
  public onPersonsChanged () {
    this.recalculateTotals()
  }

  @Watch('roomId')
  public async onRoomChanged () {
    await this.fetchUnavailableTables()
  }

  @Watch('dateFrom')
  public async onDateFromChanged () {
    await this.fetchUnavailableTables()
  }

  @Watch('dateTo')
  public async onDateToChanged () {
    await this.fetchUnavailableTables()
  }

  @Watch('timeFrom')
  public async onTimeFromChanged () {
    await this.fetchUnavailableTables()
  }

  @Watch('timeTo')
  public async onTimeToChanged () {
    await this.fetchUnavailableTables()
  }

  public formattedDateTo (): string {
    return moment(this.dateTo).format(this.DISPLAY_DATE_FORMAT)
  }

  public formattedDateFrom (): string {
    return moment(this.dateFrom).format(this.DISPLAY_DATE_FORMAT)
  }

  public formattedStatusConfirmationDate (): string {
    return moment(this.statusConfirmationDate).format(this.DISPLAY_DATE_FORMAT)
  }

  public formattedPaymentDate (index: number): string {
    return moment(this.payments[index].date).format(this.DISPLAY_DATE_FORMAT)
  }

  public formattedRefundDate (index: number): string {
    return moment(this.refunds[index].date).format(this.DISPLAY_DATE_FORMAT)
  }

  public callPhone (phone: string) {
    window.location.href = `tel:${phone}`
  }

  public showRefunds (): boolean {
    return !!this.getRefundedAmount()
  }

  private getRefundedAmount (): number | null {
    let refunded = 0
    if (this.refunds) {
      for (const [index, refund] of this.refunds.entries()) {
        if (+refund.amount) {
          refunded += +refund.amount
        }
      }
    }

    return refunded
  }

  /**
   * Created lifecycle hook. Fires when component instance is created.
   */
  public async mounted () {
    if (this.canEdit && !this.id) { // Add new event
      this.eventIsFetched = true
      this.contacts = [{ name: '', phone: '' }]
      this.options = [{ name: '', amount: '' }]
      this.persons = [{ count: '', price: '' }]
      this.payments = [{ date: null, owner: '', amount: '', type: '' }]
      this.paymentDateMenus = [false]
      this.refunds = [{ date: null, owner: '', amount: '', type: '' }]
      this.refundDateMenus = [false]
      this.menu = []
    }
  }

  public menuItems (): Array<{
    text: string,
    value: string,
    disabled: boolean,
    divider: boolean,
    header: string | null,
  }> {
    const items = []
    let lastGroupId = null
    for (const group of this.getMenuList) {
      for (const menuItem of group.items) {
        let header = null
        if (lastGroupId !== group.id) {
          lastGroupId = group.id
          header = group.title
        }
        items.push({
          text: menuItem.title + (menuItem.extraPrice ? ' (+' + menuItem.extraPrice + ' руб.)' : ''),
          value: menuItem.id,
          disabled: false,
          divider: false,
          header,
        })
      }
    }
    return items
  }

  /**
   * Created lifecycle hook. Fires when component instance is created.
   */
  public async created () {
    this.canEdit = this.editable
    await this.fetchRoomsList()
    await this.fetchMenuList()

    if (this.id) {
      await this.fetchCurrentEvent()
    }

    if (this.canEdit && !this.id) { // Add new event
      this.eventIsFetched = true
      if (this.$route.query.date) {
        const date = new Date(this.$route.query.date as string)
        this.dateFrom = moment(date.toISOString()).format('YYYY-MM-DD')
        this.dateTo = moment(date.toISOString()).format('YYYY-MM-DD')
        await this.fetchUnavailableTables()
      }
    }
  }
}
